import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { ACCOUNT_TYPES } from '../../../constants/account-constants';
import ActiveStatus from "../../../constants/status/active-status";
import { LICENSE_HID_MOBILE_ACCESS, LICENSE_STID_MOBILE } from "../../../constants/license-constants";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { MobileAccount } from "../../../models/Account";
import { EnrolmentDevice } from '../../../models/EnrolmentDevices';
import { EnrolmentTask } from '../../../models/EnrolmentTask';
import { Tag } from "../../../models/Tag";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { useGlobalState } from "../../../Providers";
import { getMobileAccounts } from "../../../services/mobileAccountService";
import { IShowEnrollValidOptions } from '../../../services/wsPluginService/plugins/base/pluginModels';
import { appendFilter } from "../../../utilities/filters";
import { useDialog } from "../../dialog/DialogContext";
import Wizard from "../../wizard/wizard";
import SubTasksForm from '../sub-tasks-form';
import SummaryForm from "./summary-form";
import SelectUsedWith from "./select-used-with";
import CaptureEmail from "./capture-email";
import { ListQuery } from "../../../models/ListQuery";
import { MobileAccountWrapper } from "../../../models/wrappers/MobileAccountWrapper";
import { MultiSelectOption } from "../../../models/MultiSelectOption";

interface Props {
    availablePlugins: IShowEnrollValidOptions;
    availableSystemDeviceIds: number[],
    onFieldReset: (event: any) => void;
    onSave: (values: any) => void;
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    cardInfo: Tag;
    onClose: () => void;
    userEmail: string;
}

const EnrolMobileWizard: FC<Props> = ({ onFieldReset, onSave, defaultContent, defaultSteps, cardInfo, onClose, userEmail, availablePlugins, availableSystemDeviceIds }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const enrolmentTask = new EnrolmentTask(translate);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [deviceType, setDeviceType] = useState('');
    const [selectedUsedWith, setSelectedUsedWith] = useState<MultiSelectOption>({ value: '', label: '' });
    const [selectedAccount, setSelectedAccount] = useState<MobileAccount>();
    const dialog = useDialog();
    const { buildInfo } = useGlobalState() ?? {};

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.taskProcedure),
            description: deviceType,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.usedWith),
            description: selectedUsedWith.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.email),
            description: userEmail,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const content = [
        ...defaultContent,
        <SubTasksForm
            onNext={(event) => onFirstLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            enrollMethod={enrolmentTask.ENROL_MOBILE_CREDENTIAL.value}
            availableSystemDeviceIds={availableSystemDeviceIds}
            pluginsAvailable={availablePlugins} />,
        <SelectUsedWith
            onBack={(event) => onLocalBack(event)}
            onNext={(event) => setStep(step + 1)}
            setSelectedUsedFor={setSelectedUsedWith}
            account={selectedAccount} />,
        <CaptureEmail
            onBack={(event) => onLocalBack(event)}
            onNext={(event) => setStep(step + 1)}
            userEmail={userEmail}
            account={selectedAccount} />,
        <SummaryForm
            task={steps.length > 0 ? steps[0].description : ''}
            subTask={steps.length > 1 ? steps[1].description : ''}
            onBack={(event) => onLocalBack(event)}
            account={selectedAccount}
            selectedUsedWith={selectedUsedWith} />
    ];

    const onFirstLocalNext = async (event) => {
        let isAccountLicensed: boolean = false;
        let account: MobileAccount | undefined = undefined;

        if (event.task === enrolmentDevice.HID_MOBILE_ACCESS.value) {
            account = await getAccount(getMobileAccounts, ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS);
            isAccountLicensed = buildInfo?.licenceIds?.includes(LICENSE_HID_MOBILE_ACCESS) ?? false;
        }
        else if (event.task == enrolmentDevice.STID_MOBILE.value) {
            account = await getAccount(getMobileAccounts, ACCOUNT_TYPES.ID_STID_MOBILE_ID);
            isAccountLicensed = buildInfo?.licenceIds?.includes(LICENSE_STID_MOBILE) ?? false;
        }

        const isAccountActive: boolean = account ? account.status === ActiveStatus.Active.id : false;

        // Check if the account is active
        if (isAccountLicensed && isAccountActive) {
            setDeviceType(event.description);
            setSelectedAccount(account);
            setStep(step + 1);
        }
        // Check if the account is licensed
        else if (!isAccountLicensed) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
            event.setTaskSelected(false);
        }
        // Otherwise, the account is inactive
        else {
            dialog.pushError({ title: translate.byKey(TranslationKeys.accountDisabled), message: translate.byKey(TranslationKeys.activeMobileAccessAccount) });
            event.setTaskSelected(false);
        }
    }

    // Function to get the mobile accounts filtered by account type
    const getAccount = async (dataMethod: (listParameters?: ListQuery) => Promise<MobileAccountWrapper>, accountTypeId: number): Promise<MobileAccount | undefined> => {
        try {
            const mobileAccount: MobileAccount[] = (await dataMethod({ filters: appendFilter(accountTypeId, 'Account Type', 'accountType.id', []) })).rows;

            if (mobileAccount.length > 0) {
                return mobileAccount[0];
            }
        } catch (error) {
            return undefined;
        }
    }

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onFieldReset(event);
            return;
        }

        setStep(step - 1);
    }

    return <Formik
        enableReinitialize={true}
        initialValues={cardInfo}
        onSubmit={onSave}>
        <Form>
            <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                step={step}
                onClose={() => onClose()}
                sidebarSteps={steps}>
                {content}
            </Wizard>
        </Form>
    </Formik>
}

export default EnrolMobileWizard;
